import React, { ReactElement, useContext, useEffect } from 'react';
import { css } from '@compiled/react';
import { Button, ButtonAppearance, ButtonSize, Eyebrow, Heading, Text } from '@atlassian/learning-components';
import { token } from '@atlaskit/tokens';
import { media } from '@atlaskit/primitives/responsive';
import { useLoaderData, useNavigate, useParams } from 'react-router-dom';

import { CertificatePreparationSection } from 'src/pages/collections/CertificationPreparation/children/CertificatePreparationSection';
import { HelmetContext } from 'src/components/Helmet';
import { AtlassianCommunitySection } from 'src/pages/collections/CertificationPreparation/children/AtlassianCommunitySection';
import { useAtlassianUser } from 'src/hooks/useAtlassianUser';
import { sendScreenEvent, sendUIEvent } from 'src/utils/analytics';
import { CertPrepLoaderProps } from 'src/pages/collections/CertificationPreparation/loader';
import { getImageAttrs } from 'src/utils/contentful';
import { AlternativeCollectionType, CollectionType } from 'src/constants';
import { getSlug } from 'src/utils/slug';
import { useRedirectObject } from 'src/hooks/useRedirectObject';
import { useAlternativeRedirect } from 'src/hooks/useAlternativeRedirect';
import { getCertificationUrl, getCollectionUrl, withPrefix } from 'src/utils/getUrl';
import { useReferrer } from 'src/hooks/useReferrer';

export const CertificatePreparationCollectionPage = (): ReactElement => {
  const { certificationPrep } = useLoaderData() as CertPrepLoaderProps;
  const { continueLearningContent, onDemandContent, prepContent, relatedCertification, groupContent, hero } =
    certificationPrep;

  const { user, isLoading, hasError } = useAtlassianUser();
  const helmet = useContext(HelmetContext);
  const navigate = useNavigate();
  const { certPrepId } = useParams();
  const { referrerPath, isInitialized } = useReferrer();

  useAlternativeRedirect({
    from: getCollectionUrl(AlternativeCollectionType.CERT_PREP, certPrepId),
    to: getCollectionUrl(CollectionType.CERT_PREP, certPrepId),
  });

  const eyebrow = relatedCertification?.certificationType?.certificationTypeNameInternal
    ? relatedCertification?.certificationType?.certificationTypeNameInternal
    : hero?.eyebrowText;

  helmet.setHelmet({
    title: `${certificationPrep.title} | Learning - Atlassian Community`,
    seoTags: certificationPrep.seo,
  });

  const redirectSlug = getSlug(certificationPrep);

  useRedirectObject({ ':certPrepId': redirectSlug });

  useEffect(() => {
    if (!isLoading && !hasError && certificationPrep && isInitialized) {
      sendScreenEvent(
        'certificationPreparationCollectionScreen',
        {
          collectionSlug: redirectSlug,
          learningReferrerPath: referrerPath,
        },
        user?.account_id,
      );
    }
  }, [user, isLoading, hasError, certificationPrep, redirectSlug, isInitialized, referrerPath]);

  const handleLearnMoreClick = (event?: React.MouseEvent): void => {
    event?.preventDefault();
    sendUIEvent('certificationPrepCollectionScreen', 'button', 'clicked', 'getStartedButton', {}, user?.account_id);
    navigate(getCertificationUrl(getSlug(relatedCertification)));
  };

  return (
    <main data-testid="cert-prep-collection" css={mainStyles}>
      <section data-testid="cert-prep-header" css={headerSectionStyles}>
        <div css={containerStyles}>
          {hero && (
            <div css={headerContentStyles}>
              <Eyebrow fontWeight="semibold" size="medium" color={token('color.text.subtle')}>
                {eyebrow}
              </Eyebrow>
              <Heading level={0}>{hero.titleExternal}</Heading>
              <Text size="large">{hero.subtitle}</Text>
              <div css={buttonStyles}>
                <Button
                  label="View exam details"
                  appearance={ButtonAppearance.PRIMARY}
                  onClick={(event) => handleLearnMoreClick(event)}
                  size={ButtonSize.LARGE}
                  isResponsive={true}
                  target="_blank"
                  href={withPrefix(getCertificationUrl(getSlug(relatedCertification)))}
                />
              </div>
            </div>
          )}
        </div>
      </section>
      <section data-testid="on-demand-content" css={certifiedSectionStyles}>
        {onDemandContent && (
          <div key={onDemandContent?.sys.id}>
            <CertificatePreparationSection
              title={onDemandContent?.contentSectionTitle?.contentSectionTitleExternal}
              imageAttrs={getImageAttrs(onDemandContent?.contentSectionTitle?.asset)}
              relatedContent={onDemandContent?.contentLinksCollection?.items}
              backgroundColor="#F8F8F8"
            />
          </div>
        )}
      </section>
      <section data-testid="prep-resources" css={certifiedSectionStyles}>
        {prepContent && (
          <div key={prepContent.sys.id}>
            <CertificatePreparationSection
              title={prepContent?.contentSectionTitle?.contentSectionTitleExternal}
              imageAttrs={getImageAttrs(prepContent?.contentSectionTitle?.asset)}
              relatedContent={prepContent?.contentLinksCollection?.items}
            />
          </div>
        )}
      </section>
      {groupContent && (
        <AtlassianCommunitySection
          title={groupContent?.contentSectionTitle?.contentSectionTitleExternal}
          imageAttrs={getImageAttrs(groupContent?.contentSectionTitle?.asset)}
          relatedContent={groupContent?.contentLinksCollection?.items}
        />
      )}
      <section data-testid="continue-learning" css={certifiedSectionStyles}>
        {continueLearningContent && (
          <div key={continueLearningContent.sys.id}>
            <CertificatePreparationSection
              title={continueLearningContent?.contentSectionTitle?.contentSectionTitleExternal}
              imageAttrs={getImageAttrs(continueLearningContent?.contentSectionTitle?.asset)}
              relatedContent={continueLearningContent?.contentLinksCollection?.items}
            />
          </div>
        )}
      </section>
    </main>
  );
};

const mainStyles = css({
  paddingTop: '136px',

  '@media (max-width: 1279px)': {
    paddingTop: '0',
  },
  marginBottom: '104px',
});

const headerSectionStyles = css({
  margin: `54px 0 ${token('space.600')} 0`,
});

const containerStyles = css({
  maxWidth: '1440px',
  width: '100%',
  margin: '0 auto',
  display: 'flex',
  flexDirection: 'column',
  textAlign: 'center',

  [media.below.lg]: {
    padding: `0 ${token('space.300')}`,
  },

  [media.below.sm]: {
    padding: `0 ${token('space.200')} !important`,
  },
});

const headerContentStyles = css({
  maxWidth: '720px',
  width: '100%',
  margin: '0 auto',
  display: 'flex',
  flexDirection: 'column',
  gap: token('space.200'),
  textAlign: 'center',
});

const buttonStyles = css({
  display: 'flex',
  justifyContent: 'center',
});

const certifiedSectionStyles = css({
  marginBottom: token('space.1000'),

  [media.below.sm]: {
    marginBottom: '56px',
  },
});
